/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useLoanContext } from '../../contexts/LoanContext';
import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, epochToDate, getErrorFromEndpoint, addToObj } from '../UtilityFunctions';
import useGetPaymentInfo from './useGetPaymentInfo';
import useGetBatchList from './useGetBatchesList';
import useAddServicingBatch from './useAddServicingBatch';
import useBatch from './useBatch';
import useEventTrigger from './useEventTrigger';

function isEpochOnCurrentDay(dateToCheck: number) {
  const epochAsDate = epochToDate(dateToCheck);
  const currdate = new Date();
  const sameMonth = epochAsDate.getUTCMonth() === currdate.getMonth();
  const sameDay = epochAsDate.getUTCDate() === currdate.getDate();
  const sameYear = epochAsDate.getUTCFullYear() === currdate.getFullYear();
  return sameMonth && sameDay && sameYear;
}

function getManualBatchCreated(batches: Array<ServicingBatch>) {
  if (batches.length > 0) {
    for (const batch of batches) {
      if (
        batch.active &&
        (batch.postedSource === '005' || batch.postedSourceKeyword === 'MANUAL') &&
        isEpochOnCurrentDay(batch.dateCreated)
      ) {
        return batch.batchNo;
      }
    }
  }
  return '';
}

const useApplyAggregateEscrowAnalysis = () => {
  const { loanNumber } = useLoanContext();
  const [applyingAEA, setApplyingAEA] = useState(false);
  const [applyAEAResp, setApplyAEAResp] = useState(null);
  const [errorApplyingAEA, setErrorApplyingAEA] = useState('');
  const { batches } = useGetBatchList();
  const { addServicingBatch } = useAddServicingBatch();
  const { getBatch } = useBatch();
  const { triggerEvent } = useEventTrigger();

  const { getPaymentInfo } = useGetPaymentInfo(loanNumber);

  const PaymentChecked = useCallback((loadPaymentRes) => {
    if (isObjEmptyOrNull(loadPaymentRes)) return false;
    if (!('active' in loadPaymentRes)) return false;
    return true;
  }, []);

  const addServicingManualBatch = useCallback(async () => {
    const requestBody = {
      active: true,
      postedSourceKeyword: 'MANUAL',
    };
    const response = await addServicingBatch(requestBody);
    if (response) {
      if (!response.batchNo) {
        return '';
      } else {
        return response.batchNo;
      }
    }
    return '';
  }, [addServicingBatch]);

  const getOccrToCreate = useCallback(async () => {
    const lastOccrObj = await triggerEvent('AEA EFFECTIVE');
    if (isObjEmptyOrNull(lastOccrObj)) return { newOccr: null };
    const err = getErrorFromEndpoint(lastOccrObj);
    if (!isStrEmptyOrNull(err)) return { newOccr: null };
    const lastOccr =
      !isObjEmptyOrNull(lastOccrObj) && 'lastOccurrence' in lastOccrObj ? lastOccrObj.lastOccurrence : null;

    if (!isStrEmptyOrNull(lastOccr)) {
      return { newOccr: lastOccr };
    }
    return { newOccr: null };
  }, [triggerEvent]);

  const getBatchNo = useCallback(async () => {
    let batchNo = getManualBatchCreated(batches);
    if (isStrEmptyOrNull(batchNo)) {
      batchNo = await addServicingManualBatch();
    }
    if (isStrEmptyOrNull(batchNo)) return { batchNo: '' };
    return { batchNo: batchNo };
  }, [addServicingManualBatch, batches]);

  const getPaymentForDay = useCallback(
    async (params: ApplyAEAParams) => {
      const loadPaymentRes = await getPaymentInfo(params.dateToCheck);
      return { loadPayment: loadPaymentRes };
    },
    [getPaymentInfo],
  );

  const getImpoundTotalData = useCallback(
    (params: ApplyAEAParams) => {
      const req = {
        impoundTotal: {
          endpoint:
            process.env.REACT_APP_IMPOUND_TOTAL.replace('live/', '').replace('?', loanNumber) + params.dateToCheck,
          data: {
            status: true,
            mnthlyImpAmt: params.newMonthlyImpound,
          },
        },
      };
      return req;
    },
    [loanNumber],
  );

  const getUpdateInitPaymentData = useCallback(
    (createPaymentRequired: boolean, params: ApplyAEAParams) => {
      let updateInitParams: PaymentInitReq = {
        totalEscrowShortage: params.escrowShortSurplus,
        escrowShortageMonthlyRepaymentAmt: params.paymentChange,
        escrowShortageRepayMonths: params.escrowShortageRepayMonths,
        eaCurrentBalance: params.currentBalance,
        eaProjectedBalance: params.projectedBalance,
        eaEstimatedBalance: params.estimatedBalance,
        eaNewEscrowPayment: params.newEscrowPayment,
        eaNewMonthlyPayment: params.newMonthlyPayment,
        escrowAnalysisTiCushion: params.escrowAnalysisTiCushion,
        eaExpectedMonthlyPayment: params.expectedMonthlyPayment,
        eaRefundNotApplicable: params.refundNotApplicable,
        eaIntendedRefundAmount: params.intendedRefund,
        eaExpectedEscrowPayment: params.expectedEscrowPayment,
        escrowShortageLpiDate: params.lastPaidInstallmentDate,
        eaPitiDiff: params.pitiDiff,
        defaultEscrowShortageMonthlyRepaymentAmt: false,
      };
      const req = {
        createOrUpdatePayment: {
          endpoint: createPaymentRequired
            ? process.env.REACT_APP_PAYMENT_INIT.replace('live/', '').replace('?', loanNumber) + params.dateToCheck
            : process.env.REACT_APP_PAYMENT_DUEDATE.replace('live/', '').replace('?', loanNumber) + params.dateToCheck,
          data: updateInitParams,
        },
      };
      return req;
    },
    [loanNumber],
  );

  const getUpdateTrialBalancesData = useCallback(
    (params: ApplyAEAParams) => {
      const req: any = {
        trialBalances: {
          endpoint:
            process.env.REACT_APP_ESCROW_MONTHLY_TRIAL_BALANCES.replace('live/', '').replace('?', loanNumber) +
            +params.dateToCheck +
            '/replace',
          data: {
            trialBalances: params.trialBalances,
          },
        },
      };
      return req;
    },
    [loanNumber],
  );

  // two event dates are used for recording
  // AEA is the effective date for which AEA was applied
  // AER is the date AEA is applied on (current date)
  // AER was created since we have to migrate the run dates from McDonald's database
  // AEA's timestamp can't be used as the run date since the timestamp is autogenerated and would
  // have to be updated through SQL and MOL data should always be updated through MOL code, per Luis
  const getAEAEventDatesReq = useCallback(
    (params: ApplyAEAParams, newOccr: string) => {
      const req: any = {
        AEAeventdate: {
          endpoint: process.env.REACT_APP_EVENT_DATES.replace('?', loanNumber) + 'AEA/occurrence/' + newOccr,
          data: {
            activeDate: params.effectiveDateUTCTimestamp,
          },
        },
        AEReventdate: {
          endpoint: process.env.REACT_APP_EVENT_DATES.replace('?', loanNumber) + 'AER/occurrence/' + newOccr,
          data: {
            activeDate: Math.round(Date.now() / 1000),
          },
        },
      };
      return req;
    },
    [loanNumber],
  );

  const getCreateRefundData = useCallback(
    (params: ApplyAEAParams, batchNo: string) => {
      const req = {
        refundTransaction: {
          endpoint:
            process.env.REACT_APP_CREATE_TRANSACTION.replace('live/', '').replace('?', batchNo) + loanNumber + '/add',
          data: {
            mcdTransType: '80',
            posted: true,
            amount: params.intendedRefund,
            payee: '999999',
          },
        },
      };
      return req;
    },
    [loanNumber],
  );

  const getBatchParams = useCallback(
    async (createPaymentRequired: boolean, params: ApplyAEAParams, batchNo: string, newOccr: string) => {
      const promises = [];
      promises.push(
        getImpoundTotalData(params),
        getUpdateInitPaymentData(createPaymentRequired, params),
        getAEAEventDatesReq(params, newOccr),
        getUpdateTrialBalancesData(params),
      );
      if (!isStrEmptyOrNull(batchNo)) {
        promises.push(getCreateRefundData(params, batchNo));
      }
      let req = null;
      await Promise.all(promises).then((res) => {
        req = addToObj(req, res);
      });

      return req;
    },
    [
      getImpoundTotalData,
      getUpdateInitPaymentData,
      getAEAEventDatesReq,
      getCreateRefundData,
      getUpdateTrialBalancesData,
    ],
  );

  const continueProcessRes = useCallback(
    async (promises: Array<any>, refundRequired: boolean = false) => {
      let continueProcessRes = null;
      await Promise.all(promises).then((res) => {
        let loadPaymentPresent = false;
        let batchNoPresent = false;
        let tempRes = {};
        if (isObjEmptyOrNull(res)) return;
        res.forEach((elem) => {
          if (isObjEmptyOrNull(elem)) return;
          if ('batchNo' in elem) {
            batchNoPresent = true;
            if (isStrEmptyOrNull(elem.batchNo)) return;
            tempRes['batchNo'] = elem.batchNo;
          }
          if ('loadPayment' in elem) {
            loadPaymentPresent = true;
            if (!PaymentChecked(elem.loadPayment)) return;
            tempRes['loadPayment'] = elem.loadPayment;
          }
          if ('newOccr' in elem) {
            if (!isStrEmptyOrNull(elem.newOccr)) tempRes['newOccr'] = elem.newOccr;
          }
        });
        if ((refundRequired && !batchNoPresent) || !loadPaymentPresent || !('newOccr' in tempRes)) return;
        continueProcessRes = tempRes;
      });
      return continueProcessRes;
    },
    [PaymentChecked],
  );

  const applyAEAAPI = useCallback(
    async (params: ApplyAEAParams) => {
      try {
        if (isObjEmptyOrNull(params)) return false;

        const promises = [];

        promises.push(getPaymentForDay(params), getOccrToCreate());

        const continueRes = await continueProcessRes(promises);
        if (isObjEmptyOrNull(continueRes)) return false;
        const newOccr = continueRes.newOccr;
        const paymentCreated = 'active' in continueRes?.loadPayment && continueRes?.loadPayment?.active;
        const batchParams = await getBatchParams(
          !paymentCreated,
          params,
          continueRes.batchNo ? continueRes.batchNo : '',
          newOccr,
        );
        const res = await getBatch(batchParams);
        if (isObjEmptyOrNull(res)) return false;
        const err = getErrorFromEndpoint(res);
        if (!isStrEmptyOrNull(err)) return false;
        for (const endpoint in res) {
          if (!isStrEmptyOrNull(getErrorFromEndpoint(res, endpoint))) return false;
        }
        setApplyAEAResp(res);
        return true;
      } catch (error) {
        console.log('Error applying:', error);
        setErrorApplyingAEA('Error applying: ' + error.toString());
        setApplyAEAResp(null);
        return false;
      }
    },
    [getBatch, getBatchNo, getBatchParams, getPaymentForDay, continueProcessRes, getOccrToCreate],
  );

  const applyAEA = useCallback(
    async (params: ApplyAEAParams) => {
      if (isStrEmptyOrNull(loanNumber) || isObjEmptyOrNull(params)) {
        setErrorApplyingAEA('Unable to process request. Please try again.');
        return false;
      }
      setApplyAEAResp(null);
      setApplyingAEA(true);
      const res = await applyAEAAPI(params).finally(() => {
        setApplyingAEA(false);
      });
      if (!res) {
        setErrorApplyingAEA('Unable to process request. Please try again.');
      } else {
        setErrorApplyingAEA('');
      }
      return res;
    },
    [applyAEAAPI, loanNumber],
  );

  return { applyingAEA, errorApplyingAEA, applyAEA, applyAEAResp };
};

export default useApplyAggregateEscrowAnalysis;
